import { useCallback, useEffect, useRef, useState } from 'react';

/** Minimale typings; Web Speech API is niet overal in TS lib aanwezig. */
type SpeechRecInstance = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecResultEvent) => void) | null;
  onerror: ((event: SpeechRecErrorEvent) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecResultEvent = {
  resultIndex: number;
  results: {
    length: number;
    [i: number]: { isFinal: boolean; 0: { transcript: string } };
  };
};

type SpeechRecErrorEvent = {
  error: string;
  message?: string;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecInstance) | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window &
    typeof globalThis & {
      SpeechRecognition?: new () => SpeechRecInstance;
      webkitSpeechRecognition?: new () => SpeechRecInstance;
    };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

export type UseSpeechRecognitionOptions = {
  lang?: string;
  getBaseText: () => string;
  onTranscript: (full: string) => void;
};

export function useSpeechRecognition({
  lang = 'nl-NL',
  getBaseText,
  onTranscript,
}: UseSpeechRecognitionOptions) {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecInstance | null>(null);
  const cumulativeFinalRef = useRef('');
  const getBaseTextRef = useRef(getBaseText);
  const onTranscriptRef = useRef(onTranscript);

  getBaseTextRef.current = getBaseText;
  onTranscriptRef.current = onTranscript;

  const stopListening = useCallback(() => {
    const r = recognitionRef.current;
    if (r) {
      try {
        r.abort();
      } catch {
        try {
          r.stop();
        } catch {
          /* ignore */
        }
      }
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Spraakherkenning wordt niet ondersteund in deze browser');
      return;
    }
    setError(null);
    cumulativeFinalRef.current = '';
    const base = getBaseTextRef.current();
    const baseWithSpace = base.endsWith(' ') || base.length === 0 ? base : `${base} `;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecResultEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const piece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          cumulativeFinalRef.current += piece;
        } else {
          interim += piece;
        }
      }
      const live = `${baseWithSpace}${cumulativeFinalRef.current}${interim}`.replace(/\s+/g, ' ');
      onTranscriptRef.current(live.trimStart());
    };

    recognition.onerror = (e: SpeechRecErrorEvent) => {
      if (e.error === 'aborted' || e.error === 'no-speech') return;
      if (e.error === 'not-allowed') {
        setError('Microfoon toegang geweigerd');
      } else {
        setError(e.message || e.error);
      }
      stopListening();
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch {
      setError('Spraak kon niet worden gestart');
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, [lang, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, startListening, stopListening]);

  useEffect(() => () => stopListening(), [stopListening]);

  return {
    supported: isSpeechRecognitionSupported(),
    isListening,
    error,
    startListening,
    stopListening,
    toggleListening,
    clearError: () => setError(null),
  };
}
